import { useUrlQuery } from '@core/base/composables/urlQuery';
import { entryPathWasQuizStartPage, entryQueryParams } from '../common/entryContext';

export async function useInitApp() {
  const route = useRoute();
  const { saveQuery } = useUrlQuery();
  const {
    $mixpanel,
    $versions,
    $sentrySetUser,
    $snapTrack,
    $releaseVersion,
    $ttIdentify,
    $logger,
  } = useNuxtApp();
  const store = useKegelAnalyticStore();
  const { initPixel } = useFacebookPixel();
  const { generateFunnel, experimentId, variation } = useVersions();
  const { sendAttribution, setUserProperty } = useMixpanel();
  const { track, trackStartScreenShown } = useEventsDispatcher();
  const { getClickID } = useClickID();
  const { locale } = useI18n();
  const API = useKegelApi();
  const { initGtag } = useCustomGtag();
  const posthog = usePostHog();
  const funnelPrefix = useFunnelPrefix();
  const { initLogRocket } = useLogRocket();

  if (import.meta.client) {
    $logger.note({
      category: 'init_app',
      message: 'Client init started',
      level: 'info',
      data: {
        path: route.path,
      },
    });

    setUserProperty({ 'Release version': $releaseVersion });
    const clickIDPromise = getClickID(experimentId, variation);

    await API.getUserData();
    const [, clickIdEarly] = await Promise.all([initPixel(), clickIDPromise]);
    $snapTrack('PAGE_VIEW', {});

    if (clickIdEarly) {
      store.clickId = clickIdEarly;
    }

    if (entryPathWasQuizStartPage(funnelPrefix) && !entryQueryParams().get('attribution')) {
      track('Quiz_Start_Screen_Shown');
    }
  }

  async function callActionsAfterMounted() {
    if (!import.meta.client) {
      return;
    }

    if (store.isLogRocketEnabled) {
      await initLogRocket();
    }
    trackWebAppStarted();
    checkQueryParams();
    scheduleGtagInit();
    await setUserDataToTrackers();

    const clickId = await syncClickIdState();

    await sendAttributionIfNeeded(clickId);
    saveQuery(buildMissingQueryParams(clickId));
  }

  function checkQueryParams(): void {
    if (route.query?.email) {
      store.userEmail = route.query?.email as string;
      $ttIdentify({ email: route.query?.email as string });
    }
  }

  function trackWebAppStarted(): void {
    const pageOpenTime = (globalThis as any).__aoPageOpenTime as number | undefined;

    if (!pageOpenTime) {
      return;
    }

    track('WebApp Started', {
      webapp_start_ms: Date.now() - pageOpenTime,
    });
  }

  function scheduleGtagInit(): void {
    setTimeout(() => {
      initGtag();
    }, 5000);
  }

  async function syncClickIdState(): Promise<string> {
    const clickId = await getClickID(experimentId, variation) as string;

    if (experimentId.value && !store.experiments.some(e => e.experimentId === experimentId.value)) {
      store.experiments.push({ experimentId: experimentId.value, variation: variation.value || '' });
    }

    store.clickId = clickId;

    return clickId;
  }

  async function sendAttributionIfNeeded(clickId: string): Promise<void> {
    if ($versions.value.trackOnlyAfterQuiz === '0') {
      return;
    }

    await sendAttribution(clickId);
  }

  function buildMissingQueryParams(clickId: string): { [key: string]: string } {
    const queries: { [key: string]: string } = {};

    if (experimentId.value && variation.value) {
      queries.experimentId = experimentId.value;
      queries.variation = variation.value;
    }

    if (clickId && route.query.rtkcid !== clickId) {
      queries.rtkcid = clickId;
    }

    if (!route.query.attribution && store.attributesWasSet) {
      queries.attribution = '1';
    }

    if (!route.query.uuid && store.userUuid) {
      queries.uuid = store.userUuid;
    }

    return queries;
  }

  async function setUserDataToTrackers(): Promise<void> {
    if ($versions.value.trackOnlyAfterQuiz !== '0') {
      $mixpanel.identify(store.userUuid);

      if (store.userEmail) {
        setUserProperty({ $email: store.userEmail });
      }
      setUserProperty({ 'Quiz Language': locale.value?.toUpperCase() });
    }
    posthog?.identify(store.userUuid);
    $sentrySetUser({ id: store.userUuid });
    await generateFunnel();
    trackStartScreenShown();
    posthog?.setPersonProperties({ Funnel: $versions?.value.funnel || '' });
    posthog?.capture('$pageview');
  }

  return { callActionsAfterMounted };
}
